import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

/**
 * FAQ Component
 * Frequently asked questions about services and compliance
 */
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null)

  const faqs = [
    {
      question: 'What regulatory standards do you comply with?',
      answer: 'We ensure compliance with FDA (U.S.), CDSCO (India), EU Regulations, and GCC Guidelines. Our team stays current with evolving standards to ensure your packaging meets the latest legal, safety, and accessibility requirements.',
    },
    {
      question: 'Do you handle multilingual packaging?',
      answer: 'Yes, we specialize in multilingual packaging including Arabic (right-to-left formatting), Hindi, French, Spanish, and more. We ensure translations meet local labeling laws while maintaining visual consistency and cultural appropriateness.',
    },
    {
      question: 'What file formats do you deliver?',
      answer: 'We deliver print-ready files in all standard formats required by vendors and CMOs. This includes PDF, AI, EPS, and other formats as specified by your production requirements. All files are optimized for print and vendor specifications.',
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on complexity, scope, and regulatory requirements. Simple projects may take 5-7 business days, while complex multinational rollouts may require several weeks. We work closely with you to establish realistic timelines and deliver on schedule.',
    },
    {
      question: 'Can you work with existing brand guidelines?',
      answer: 'Absolutely. We work within your existing brand guidelines while ensuring regulatory compliance. Our approach maintains brand integrity while adapting to local markets, languages, and regulatory requirements.',
    },
    {
      question: 'What industries do you serve?',
      answer: 'We specialize in four high-stakes sectors: Pharmaceuticals (life-critical accuracy), Food (clarity and compliance), FMCG (speed and consistency), and Global Markets (localization and cultural adaptation).',
    },
    {
      question: 'How do you ensure artwork accuracy?',
      answer: 'Every project goes through our rigorous compliance-driven design process: briefing & regulatory mapping, artwork development aligned with authority requirements, multilingual integration with cultural sensitivity, and final prepress & QA review. No surprises, no rework.',
    },
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes, we offer ongoing maintenance and support for global portfolios. Whether you need updates for regulatory changes, new market entries, or SKU expansions, we maintain consistency across formats, regions, and vendors.',
    },
  ]

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section
      id="faq"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white border-t border-gray-200/50 relative overflow-hidden"
      aria-labelledby="faq-heading"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            id="faq-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about our packaging artwork services
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 shadow-sm hover:shadow-xl hover:scale-[1.01]"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset hover:bg-primary-50/30 transition-colors duration-300"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <span className="font-semibold text-lg text-gray-900 pr-8">
                  {faq.question}
                </span>
                <motion.svg
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-6 h-6 text-primary flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </motion.svg>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    id={`faq-answer-${index}`}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 pt-0">
                      <div className="border-t border-gray-100 pt-5">
                        <p className="text-gray-700 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Bottom decorative divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent"></div>
    </section>
  )
}

export default FAQ

